import { FC, useCallback, useEffect, useMemo } from "react";
import { Form, Formik } from "formik";
import {
  paymentInitialValues,
  paymentValidationSchema
} from "app/data/helpers/activationPortalHelpers";
import {
  findPaymentMethod,
  paymentMethodInitialValues,
  paymentMethodValidationSchema
} from "app/data/helpers/activationPaymentHelpers";
import StepScreenLayout from "components/layouts/StepScreenLayout/StepScreenLayout";
import InnerForm from "./children/InnerForm";
import PaymentMethodsInnerForm from "./children/PaymentMethodsInnerForm";

//Data
import { useAppDispatch, useAppSelector } from "hooks";
import { getPaymentMethodsThunk, setSubscribeFormData } from "app/features";
import { IActivationPayment } from "app/data/types";
import {
  selectActivationPaymentState,
  selectActivationPortalState
} from "app/data/selectors/activationPortalSelectors";
import { isActivationPaymentEnabled } from "app/data/services";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import ActivationPortalDraftFormObserver from "../ActivationPortalDraftFormObserver";

interface Props {
  gotoNextStep: () => void;
}

const ActivationPaymentForm: FC<Props> = ({ gotoNextStep }) => {
  const dispatch = useAppDispatch();

  const { subscribeFormData, verificationCode } = useAppSelector(
    selectActivationPortalState
  );
  const { methodsCatalog, selectedPaymentMethod } = useAppSelector(
    selectActivationPaymentState
  );

  const { t } = useTranslation([
    "translation",
    "activationPortal",
    "formFieldsTranslation"
  ]);

  // Both flows coexist while the feature is flagged: the legacy single-method
  // step stays live until the catalog endpoint exists.
  const withPaymentMethods = isActivationPaymentEnabled();

  const loadPaymentMethods = useCallback(() => {
    if (!withPaymentMethods) return;
    dispatch(getPaymentMethodsThunk({ activationCode: verificationCode }));
  }, [dispatch, verificationCode, withPaymentMethods]);

  // Fetched once on entering the step; eligibility is then evaluated locally,
  // so editing the IBAN costs no requests.
  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const onSubmitHandler = (values: IActivationPayment) => {
    dispatch(setSubscribeFormData(values));
    gotoNextStep();
  };

  const formValues = useMemo(() => {
    const initialValues = withPaymentMethods
      ? paymentMethodInitialValues
      : paymentInitialValues;

    return !isEmpty(subscribeFormData) && subscribeFormData?.accountHolderName
      ? { ...initialValues, ...subscribeFormData }
      : initialValues;
  }, [subscribeFormData, withPaymentMethods]);

  /**
   * The schema varies by selected method, and the selection is mirrored in the
   * store precisely so it can be read here — Formik cannot build a schema out
   * of its own values.
   */
  const validationSchema = useMemo(() => {
    if (!withPaymentMethods) return paymentValidationSchema(t);

    return paymentMethodValidationSchema(
      t,
      findPaymentMethod(methodsCatalog, selectedPaymentMethod)
    );
  }, [withPaymentMethods, t, methodsCatalog, selectedPaymentMethod]);

  return (
    <Formik
      //@ts-ignore
      initialValues={formValues}
      onSubmit={onSubmitHandler}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ handleSubmit, isValid }) => (
        <Form>
          <StepScreenLayout
            title={t("titles.payment", {
              ns: "activationPortal"
            })}
            subtitle={t("descriptions.payment", {
              ns: "activationPortal"
            })}
            disabled={!isValid}
            onButtonSubmit={handleSubmit}
          >
            {withPaymentMethods ? (
              <PaymentMethodsInnerForm
                translator={t}
                onRetryCatalog={loadPaymentMethods}
              />
            ) : (
              <InnerForm translator={t} />
            )}
          </StepScreenLayout>
          <ActivationPortalDraftFormObserver />
        </Form>
      )}
    </Formik>
  );
};

export default ActivationPaymentForm;
